import { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import { useParams, useHistory } from 'react-router-dom';
import CommonHeader from '../../../components/Headers/CommonHeader';
import OpacityLoaderSpinner from '../../../components/Core/OpacityLoaderSpinner';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import TaskBoardService from '../../../services/TaskBoard';
import TaskBoardKanban from './TaskBoardKanban';
import TaskBoardCardViewModal, { ConcludedCardDetail } from './TaskBoardCardViewModal';
import { CardComment } from './TaskBoardCardModal';
import ConcludedBoard, { ConcludedGroup } from './ConcludedBoard';
import RestoreCardModal from './RestoreCardModal';
import BoardSettingsModal from './BoardSettingsModal';
import './task-board.scss';

type Tab = 'main' | 'concluded';

type MainListOption = { id: string; name: string };

const TaskBoardPage = () => {
  const { schoolId } = useParams<{ schoolId: string }>();
  const { token, currentSchool, user } = useContext(AuthContext);
  const history = useHistory();

  useEffect(() => {
    const activeSchoolId = currentSchool?.value || user?.employee?.schoolId;
    if (activeSchoolId && activeSchoolId !== schoolId) {
      history.replace(`/task-board/${activeSchoolId}`);
    }
  }, [currentSchool, user, schoolId, history]);

  const [tab, setTab] = useState<Tab>('main');

  const [board, setBoard] = useState<any>(null);
  const [mainLoading, setMainLoading] = useState(true);
  const [mainError, setMainError] = useState('');

  const [concluded, setConcluded] = useState<any>(null);
  const [concludedLoading, setConcludedLoading] = useState(false);
  const [concludedError, setConcludedError] = useState('');

  const [viewCard, setViewCard] = useState<ConcludedCardDetail | null>(null);
  const [viewCardComments, setViewCardComments] = useState<CardComment[]>([]);
  const [loadingViewCardComments, setLoadingViewCardComments] = useState(false);

  const [restoreCard, setRestoreCard] = useState<ConcludedCardDetail | null>(null);
  const [targetListId, setTargetListId] = useState('');
  const [busy, setBusy] = useState(false);

  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [savingBoardSettings, setSavingBoardSettings] = useState(false);

  const loadMain = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!token || !schoolId) {
        setBoard(null);
        setMainError('Missing school context to load task board.');
        setMainLoading(false);
        return;
      }
      if (!options?.silent) setMainLoading(true);
      setMainError('');
      try {
        const envelope = await TaskBoardService(token).getMainBoard(schoolId);
        const payload = envelope?.data ?? envelope;
        setBoard(payload?.id && Array.isArray(payload?.lists) ? payload : null);
      } catch (e: any) {
        const status = e?.response?.status;
        const apiMessage = e?.response?.data?.message;
        if (status === 403 || status === 401) {
          setMainError('You do not have permission to access this school task board.');
        } else if (status === 404) {
          setMainError('School not found for task board.');
        } else if (apiMessage) {
          setMainError(apiMessage);
        } else {
          setMainError('Unable to load task board.');
        }
        setBoard(null);
      } finally {
        setMainLoading(false);
      }
    },
    [token, schoolId],
  );

  const loadConcluded = useCallback(async () => {
    if (!token || !schoolId) {
      setConcluded(null);
      setConcludedError('Missing school context to load concluded board.');
      setConcludedLoading(false);
      return;
    }
    setConcludedLoading(true);
    setConcludedError('');
    try {
      const envelope = await TaskBoardService(token).getConcludedBoard(schoolId);
      const data = envelope?.data ?? envelope;
      setConcluded(data?.id && Array.isArray(data?.groups) ? data : null);
    } catch (e: any) {
      const status = e?.response?.status;
      const apiMessage = e?.response?.data?.message;
      if (status === 403 || status === 401) {
        setConcludedError('You do not have permission to access this concluded board.');
      } else if (apiMessage) {
        setConcludedError(apiMessage);
      } else {
        setConcludedError('Unable to load concluded board.');
      }
      setConcluded(null);
    } finally {
      setConcludedLoading(false);
    }
  }, [token, schoolId]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  useEffect(() => {
    if (!viewCard?.id || !token || !schoolId) {
      setViewCardComments([]);
      return;
    }
    setLoadingViewCardComments(true);
    TaskBoardService(token)
      .listComments(schoolId, viewCard.id)
      .then((result) => setViewCardComments(result?.data ?? result ?? []))
      .catch(() => toast.error('Could not load comments.'))
      .finally(() => setLoadingViewCardComments(false));
  }, [viewCard?.id, token, schoolId]);

  const handleTabChange = (next: Tab) => {
    setTab(next);
    if (next === 'concluded') {
      loadConcluded();
    } else {
      loadMain();
    }
  };

  const handleSaveBoardSettings = async (settings: { listId: string; memberUserIds: string[] }[]) => {
    if (!token || !schoolId) return;
    setSavingBoardSettings(true);
    try {
      await Promise.all(
        settings.map((s) =>
          TaskBoardService(token).updateListSettings(schoolId, s.listId, { defaultMemberUserIds: s.memberUserIds }),
        ),
      );
      await loadMain();
      setShowBoardSettings(false);
    } catch {
      toast.error('Could not save board settings.');
    } finally {
      setSavingBoardSettings(false);
    }
  };

  const mainLists: MainListOption[] = concluded?.mainBoardLists ?? [];
  const groups: ConcludedGroup[] = concluded?.groups ?? [];

  const openRestore = (card: ConcludedCardDetail) => {
    setViewCard(null);
    setRestoreCard(card);
    setTargetListId(mainLists[0]?.id ?? '');
  };

  const confirmRestore = async () => {
    if (!token || !schoolId || !restoreCard?.id || !targetListId) return;
    setBusy(true);
    try {
      await TaskBoardService(token).restoreCard(schoolId, restoreCard.id, { listId: targetListId });
      toast.success('Card restored to the main board.');
      setRestoreCard(null);
      setTargetListId('');
      await Promise.all([loadMain(), loadConcluded()]);
    } catch {
      toast.error('Could not restore card.');
    } finally {
      setBusy(false);
    }
  };

  const tabBar = (
    <div className="task-board__tab-strip">
      <button
        type="button"
        className={`task-board__tab${tab === 'main' ? ' task-board__tab--active' : ''}`}
        onClick={() => handleTabChange('main')}
      >
        Main Board
      </button>
      <button
        type="button"
        className={`task-board__tab${tab === 'concluded' ? ' task-board__tab--active' : ''}`}
        onClick={() => handleTabChange('concluded')}
      >
        Concluded
      </button>
      <Button
        type="button"
        color="secondary"
        outline
        size="sm"
        className="task-board__board-settings-btn"
        title="Board Auto-Assignment Settings"
        onClick={() => setShowBoardSettings(true)}
      >
        <i className="fas fa-cog" />
      </Button>
    </div>
  );

  if (mainLoading) {
    return (
      <>
        <CommonHeader />
        <OpacityLoaderSpinner />
      </>
    );
  }

  if (!board && tab === 'main') {
    return (
      <>
        <CommonHeader />
        <div className="container-fluid mt--8 pb-6 mx-1 task-board">
          <div className="task-board__board-header">
            <h2 className="task-board__board-title">Task Board</h2>
          </div>
          {tabBar}
          <p className="text-muted">{mainError || 'Unable to load task board.'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <CommonHeader />
      <div className="container-fluid mt--8 pb-6 mx-1 task-board">
        {tab === 'main' ? (
          <TaskBoardKanban
            key={schoolId}
            board={board}
            schoolId={schoolId}
            onRefresh={loadMain}
            onSilentRefresh={() => loadMain({ silent: true })}
            tabBar={tabBar}
            onCompleted={() => {
              void loadMain();
            }}
          />
        ) : (
          <>
            <div className="task-board__board-header">
              <h2 className="task-board__board-title">{concluded?.name ?? 'Concluded'}</h2>
            </div>
            {tabBar}

            {concludedLoading ? (
              <div className="py-4 text-center text-muted">Loading…</div>
            ) : concludedError ? (
              <div className="text-danger">{concludedError}</div>
            ) : groups.length === 0 ? (
              <p className="text-muted mb-0">No concluded cards yet.</p>
            ) : (
              <ConcludedBoard groups={groups} onCardClick={setViewCard} />
            )}
          </>
        )}
      </div>

      <TaskBoardCardViewModal
        isOpen={!!viewCard}
        card={viewCard}
        onClose={() => setViewCard(null)}
        onRestore={openRestore}
        comments={viewCardComments}
        loadingComments={loadingViewCardComments}
        boardMembers={board?.members ?? []}
      />

      <RestoreCardModal
        card={restoreCard}
        mainLists={mainLists}
        busy={busy}
        targetListId={targetListId}
        onTargetChange={setTargetListId}
        onCancel={() => setRestoreCard(null)}
        onConfirm={() => void confirmRestore()}
      />

      <BoardSettingsModal
        isOpen={showBoardSettings}
        onClose={() => setShowBoardSettings(false)}
        lists={board?.lists ?? []}
        boardMembers={board?.members ?? []}
        onSave={handleSaveBoardSettings}
        saving={savingBoardSettings}
      />
    </>
  );
};

export default TaskBoardPage;
